// This file configures the initialization of Sentry on the client.
// The config here will be used whenever a user loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";

const sentryDsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

if (!sentryDsn) {
    console.warn(
        "Sentry DSN not found in environment variables. Sentry will not be initialized.",
    );
} else {
    Sentry.init({
        dsn: sentryDsn,

        environment: process.env.NODE_ENV,
        enabled: process.env.NODE_ENV === "production",

        integrations: [Sentry.replayIntegration()],

        tracesSampleRate: 1,

        // Capture 10% of sessions for replay, 100% when an error occurs
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,

        debug: false,

        denyUrls: [
            // Meta's Event Setup Tool (Events Manager -> "Track New Button"),
            // injected into the page as signals/iwl.js while an admin visually
            // configures pixel events. It throws once per animation frame:
            // ~3000 events across 7 issues between 2026-07-23 and 07-29, from a
            // single desktop session, none from real users. It also carried a
            // per-session Meta access_token in its query string, which Sentry
            // then stored in every stack frame — filtering by URL keeps those
            // out too.
            //
            // Scoped by URL, not by message: "e.getBoundingClientRect is not a
            // function" thrown from our own code is a real bug and must still
            // report.
            /\/signals\/iwl\.js/,
        ],

        ignoreErrors: [
            // Stale chunk after a deploy: a client on the previous build
            // requests a now-deleted content-hashed chunk. Not code-actionable
            // and auto-recovered via a one-time reload (see lib/chunk-error.ts).
            "ChunkLoadError",
            "Loading chunk",
            "Failed to load chunk",
            "Loading CSS chunk",
            "error loading dynamically imported module",
            "Importing a module script failed",
            // Android WebView (Facebook/Instagram in-app browser) JS bridge:
            // the host tears the bridge object down while our page is still
            // alive, so any injected callback throws. The method name varies
            // per WebView build (enableDidUserTypeOnKeyboardLogging,
            // enableButtonsClickedMetaDataLogging, postMessage,
            // sendPromoExtensionPromoCodeAutofillResult, …), so match the
            // bridge failure itself rather than each name. Not actionable.
            /Java object is gone/,
            /Java bridge method invocation error/,
            /Java exception was raised during method invocation/,
            // iOS Instagram/Facebook in-app browser WebKit bridge - same deal,
            // and the handler is often indexed (messageHandlers[e], [t],
            // [IOS_BRIDGE_NO_REPLY]) so an exact-string match misses most of them.
            /window\.webkit\.messageHandlers/,
            // Crypto wallet browser extension injecting itself into pages that
            // never ask for a wallet. Entirely third-party.
            "Failed to connect to MetaMask",
            // Embedded/third-party-cookie-blocked context (in-app browsers,
            // partitioned iframes) where storage access is denied outright.
            "Failed to read the 'localStorage' property from 'Window'",
            // CookieYes banner complaining its registered domain doesn't match
            // the current host - a console warning from their script, not a fault.
            "Looks like your website URL has changed",
            // Google reCAPTCHA internal error - thrown from third-party script,
            // likely a GTM tag or browser extension calling grecaptcha.render() without sitekey
            "Missing required parameters: sitekey",
            // React hydration mismatches (#418/#423/#425): a TEXT-content mismatch on the
            // public homepage, minified with no component stack. No render-time hazard exists
            // in our homepage components — these are overwhelmingly browser extensions and
            // in-page auto-translate (e.g. Google Translate) mutating text nodes before
            // hydration. Not code-actionable in prod. To hunt a genuine one, reproduce in
            // dev (non-minified) — it names the component. See react.dev/errors/418.
            "Minified React error #418",
            "Minified React error #423",
            "Minified React error #425",
        ],
    });
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
